import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../lib/AuthContext';
import supabase from '../lib/supabase';

const S = {
  chatWrap: (collapsed) => ({
    width: collapsed ? '48px' : '280px',
    flexShrink: 0,
    background: '#1a1d24',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    height: collapsed ? '48px' : 'calc(100vh - 56px)',
    position: collapsed ? 'relative' : 'sticky',
    top: '56px',
    marginLeft: 'auto',
    transition: 'width 0.2s, height 0.2s',
    overflow: 'hidden',
  }),
  header: {
    padding: '14px 16px',
    borderBottom: '1px solid #2a2d35',
    fontSize: '14px',
    fontWeight: 700,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    minHeight: '48px',
  },
  collapseBtn: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: '18px',
    padding: '0 4px',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  msgWrap: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    flexShrink: 0,
  },
  msgContent: {
    flex: 1,
    minWidth: 0,
  },
  username: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#00e701',
    cursor: 'pointer',
    marginBottom: '2px',
  },
  message: {
    fontSize: '13px',
    color: '#d1d5db',
    wordBreak: 'break-word',
  },
  inputWrap: {
    padding: '12px',
    borderTop: '1px solid #2a2d35',
  },
  inputRow: {
    display: 'flex',
    gap: '8px',
  },
  input: {
    flex: 1,
    background: '#0f1115',
    border: '1px solid #2a2d35',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '13px',
    padding: '8px 10px',
    outline: 'none',
  },
  sendBtn: {
    background: '#00e701',
    color: '#000',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  disabledInput: {
    flex: 1,
    background: '#0f1115',
    border: '1px solid #2a2d35',
    borderRadius: '6px',
    color: '#6b7280',
    fontSize: '13px',
    padding: '8px 10px',
    cursor: 'not-allowed',
  },
  lockedMsg: {
    fontSize: '11px',
    color: '#6b7280',
    textAlign: 'center',
    padding: '8px 12px',
    background: 'rgba(252,177,15,0.08)',
    borderRadius: '6px',
    margin: '0 12px 12px',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalCard: {
    background: '#1a1d24',
    borderRadius: '16px',
    padding: '24px',
    width: '340px',
    maxWidth: '90vw',
    border: '1px solid #2a2d35',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  modalAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 700,
    color: '#fff',
  },
  modalUsername: {
    fontSize: '18px',
    fontWeight: 800,
    color: '#fff',
  },
  modalJoined: {
    fontSize: '12px',
    color: '#6b7280',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #2a2d35',
    fontSize: '13px',
  },
  statLabel: { color: '#6b7280' },
  statValue: { color: '#fff', fontWeight: 600 },
  privateStat: { color: '#9ca3af', fontStyle: 'italic' },
  closeBtn: {
    width: '100%',
    background: '#2a2d35',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '16px',
  },
};

function getAvatarColor(username) {
  const colors = [
    ['#667eea', '#764ba2'],
    ['#f093fb', '#f5576c'],
    ['#4facfe', '#00f2fe'],
    ['#43e97b', '#38f9d7'],
    ['#fa709a', '#fee140'],
    ['#a8edea', '#fed6e3'],
    ['#ff9a9e', '#fecfef'],
    ['#ffecd2', '#fcb69f'],
  ];
  const idx = username ? username.charCodeAt(0) % colors.length : 0;
  return colors[idx];
}

function getInitials(username) {
  return username ? username.slice(0, 2).toUpperCase() : '??';
}

export default function Chat({ collapsible = true }) {
  const { profile, refreshProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [totalWagered, setTotalWagered] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserStats, setSelectedUserStats] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const messagesEndRef = useRef(null);
  const [chatReady, setChatReady] = useState(false);

  useEffect(() => {
    if (!profile) return;
    loadMessages();
    loadUserStats();
    setChatReady(true);
    
    const channel = supabase
      .channel('chat')
      .on('broadcast', { event: 'new_message' }, (payload) => {
        setMessages(prev => [...prev.slice(-99), payload.payload]);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadMessages() {
    const { data } = await supabase
      .from('chat_messages')
      .select('*')
      .order('created_at', { ascending: true })
      .limit(100);
    setMessages(data || []);
  }

  async function loadUserStats() {
    if (!profile) return;
    const { data: games } = await supabase
      .from('games')
      .select('bet_amount')
      .eq('user_id', profile.id);
    const wagered = (games || []).reduce((s, g) => s + Number(g.bet_amount), 0);
    setTotalWagered(wagered);
  }

  async function sendMessage() {
    if (!input.trim() || loading) return;
    if (totalWagered < 100) return;

    setLoading(true);
    const msg = input.trim().slice(0, 200);
    setInput('');

    const { error } = await supabase.from('chat_messages').insert({
      user_id: profile.id,
      username: profile.username,
      message: msg,
    });

    if (!error) {
      await loadMessages();
    }
    setLoading(false);
  }

  async function viewProfile(userId, username) {
    setSelectedUser({ id: userId, username });
    setShowModal(true);

    const { data: games } = await supabase
      .from('games')
      .select('*')
      .eq('user_id', userId);

    const { data: user } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (games && user) {
      const wins = games.filter(g => g.won);
      const losses = games.filter(g => !g.won);
      const totalWagered = games.reduce((s, g) => s + Number(g.bet_amount), 0);
      const totalPayout = wins.reduce((s, g) => s + Number(g.payout), 0);

      setSelectedUserStats({
        games: games.length,
        wins: wins.length,
        losses: losses.length,
        wagered: totalWagered,
        profit: totalPayout - (games.length - wins.length) * (totalWagered / games.length),
        hideStats: user.hide_stats || false,
        created_at: user.created_at,
      });
    }
  }

  const canChat = totalWagered >= 100;
  const [colors] = useState(getAvatarColor(profile?.username));

  return (
    <>
      <div style={S.chatWrap(collapsed)}>
        <div style={S.header} onClick={() => setCollapsed(!collapsed)}>
          {!collapsed && <span>Chat</span>}
          <button style={S.collapseBtn} onClick={(e) => { e.stopPropagation(); setCollapsed(!collapsed); }}>
            {collapsed ? '💬' : '−'}
          </button>
        </div>

        {!collapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <div style={S.messages}>
              {messages.map((msg, i) => {
                const msgColors = getAvatarColor(msg.username);
                return (
                  <div key={msg.id || i} style={S.msgWrap}>
                    <div
                      style={{ ...S.avatar, background: `linear-gradient(135deg, ${msgColors[0]} 0%, ${msgColors[1]} 100%)` }}
                      onClick={() => viewProfile(msg.user_id, msg.username)}
                    >
                      {getInitials(msg.username)}
                    </div>
                    <div style={S.msgContent}>
                      <div
                        style={S.username}
                        onClick={() => viewProfile(msg.user_id, msg.username)}
                      >
                        {msg.username}
                      </div>
                      <div style={S.message}>{msg.message}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {!canChat && (
              <div style={S.lockedMsg}>
                Wager ₿100 to unlock chat
              </div>
            )}

            <div style={S.inputWrap}>
              <div style={S.inputRow}>
                {canChat ? (
                  <>
                    <input
                      style={S.input}
                      placeholder="Type a message..."
                      value={input}
                      onChange={e => setInput(e.target.value.slice(0, 200))}
                      onKeyDown={e => e.key === 'Enter' && sendMessage()}
                    />
                    <button style={S.sendBtn} onClick={sendMessage} disabled={loading}>
                      Send
                    </button>
                  </>
                ) : (
                  <input
                    style={S.disabledInput}
                    placeholder={`Wager ₿100 to chat (${totalWagered}/100)`}
                    disabled
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && selectedUser && (
        <div style={S.modal} onClick={() => setShowModal(false)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div style={S.modalAvatar}>
                {getInitials(selectedUser.username)}
              </div>
              <div>
                <div style={S.modalUsername}>{selectedUser.username}</div>
                <div style={S.modalJoined}>
                  Joined {selectedUserStats?.created_at ? new Date(selectedUserStats.created_at).toLocaleDateString() : 'Unknown'}
                </div>
              </div>
            </div>

            {selectedUserStats?.hideStats ? (
              <div style={{ ...S.statRow, justifyContent: 'center' }}>
                <span style={S.privateStat}>🔒 This profile is private</span>
              </div>
            ) : (
              <>
                <div style={S.statRow}>
                  <span style={S.statLabel}>Games Played</span>
                  <span style={S.statValue}>{selectedUserStats?.games || 0}</span>
                </div>
                <div style={S.statRow}>
                  <span style={S.statLabel}>Wins</span>
                  <span style={S.statValue}>{selectedUserStats?.wins || 0}</span>
                </div>
                <div style={S.statRow}>
                  <span style={S.statLabel}>Losses</span>
                  <span style={S.statValue}>{selectedUserStats?.losses || 0}</span>
                </div>
                <div style={S.statRow}>
                  <span style={S.statLabel}>Total Wagered</span>
                  <span style={S.statValue}>₿{selectedUserStats?.wagered?.toFixed(2) || '0.00'}</span>
                </div>
                <div style={S.statRow}>
                  <span style={S.statLabel}>Net Profit</span>
                  <span style={{
                    ...S.statValue,
                    color: (selectedUserStats?.profit || 0) >= 0 ? '#00e701' : '#ff4444'
                  }}>
                    {(selectedUserStats?.profit || 0) >= 0 ? '+' : ''}₿{(selectedUserStats?.profit || 0).toFixed(2)}
                  </span>
                </div>
              </>
            )}

            <button style={S.closeBtn} onClick={() => setShowModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
